import React, { useState, useEffect, useRef } from 'react'
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Wrench
} from 'lucide-react'
import DataTable from '../../../components/DataTable/DataTable'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import { toast } from 'react-toastify'
import assetAcquisitionService from '../../../services/assetAcquisitionService'
import assetDisposalService from '../../../services/assetDisposalService'
import assetMaintenanceService from '../../../services/assetMaintenanceService'
import { formatAmount } from '../assetUtils'
// Consolidated CSS is imported globally

const FinancialReport = () => {
  const [summary, setSummary] = useState([])
  const [loading, setLoading] = useState(true)
  const [totals, setTotals] = useState({
    acquisitionCost: 0,
    disposalValue: 0,
    maintenanceCost: 0,
    acquisitionCount: 0,
    disposalCount: 0,
    maintenanceCount: 0
  })
  const hasLoadedRef = useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [acquisitionStats, disposalStats, maintenanceStats] = await Promise.all([
        assetAcquisitionService.getAcquisitionStats(),
        assetDisposalService.getDisposalStats(),
        assetMaintenanceService.getMaintenanceStats()
      ])

      const acquisitionCost = acquisitionStats.success ? (acquisitionStats.data?.totalCost || 0) : 0
      const disposalValue = disposalStats.success ? (disposalStats.data?.totalValue || 0) : 0
      const maintenanceCost = maintenanceStats.success ? (maintenanceStats.data?.totalCost || 0) : 0
      const acquisitionCount = acquisitionStats.success ? (acquisitionStats.data?.totalAcquisitions || 0) : 0
      const disposalCount = disposalStats.success ? (disposalStats.data?.totalDisposals || 0) : 0
      const maintenanceCount = maintenanceStats.success ? (maintenanceStats.data?.totalMaintenance || 0) : 0

      setTotals({ acquisitionCost, disposalValue, maintenanceCost, acquisitionCount, disposalCount, maintenanceCount })

      setSummary([
        { id: 'acquisitions', category: 'Acquisitions', records: acquisitionCount, amount: acquisitionCost, impact: 'Outflow' },
        { id: 'maintenance', category: 'Maintenance', records: maintenanceCount, amount: maintenanceCost, impact: 'Outflow' },
        { id: 'disposals', category: 'Disposals', records: disposalCount, amount: disposalValue, impact: 'Inflow' }
      ])
    } catch (error) {
      console.error('Error loading financial report:', error)
      if (!error._errorHandled) {
        toast.error('Failed to load financial report')
      }
    } finally {
      setLoading(false)
    }
  }

  const netPosition = totals.disposalValue - totals.acquisitionCost - totals.maintenanceCost

  const columns = [
    {
      accessorKey: 'category',
      header: 'Category',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'records',
      header: 'Records',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right' }}>{getValue() || 0}</div>
      ),
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right' }}>{formatAmount(getValue())}</div>
      ),
    },
    {
      accessorKey: 'impact',
      header: 'Impact',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getValue() === 'Inflow' ? 'active' : 'pending'}`}>{getValue()}</span>
        </div>
      ),
    },
  ]

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Assets', path: '/assets' },
          { label: 'Financial Report', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Financial Report</h1>
          <p>Asset financial impact and ROI analysis</p>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <TrendingDown size={16} />
            </div>
            <div className="metric-trend negative">
              <span>Outflow</span>
            </div>
          </div>
          <div className="metric-value">{formatAmount(totals.acquisitionCost)}</div>
          <div className="metric-title">Acquisition Cost</div>
          <div className="metric-subtitle">{totals.acquisitionCount} acquisitions</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Wrench size={16} />
            </div>
            <div className="metric-trend negative">
              <span>Outflow</span>
            </div>
          </div>
          <div className="metric-value">{formatAmount(totals.maintenanceCost)}</div>
          <div className="metric-title">Maintenance Cost</div>
          <div className="metric-subtitle">{totals.maintenanceCount} records</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <TrendingUp size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Inflow</span>
            </div>
          </div>
          <div className="metric-value">{formatAmount(totals.disposalValue)}</div>
          <div className="metric-title">Disposal Value</div>
          <div className="metric-subtitle">{totals.disposalCount} disposals</div>
        </div>

        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className={`metric-trend ${netPosition >= 0 ? 'positive' : 'negative'}`}>
              <span>Net</span>
            </div>
          </div>
          <div className="metric-value">{formatAmount(netPosition)}</div>
          <div className="metric-title">Net Position</div>
          <div className="metric-subtitle">Disposals less costs</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={summary}
          columns={columns}
          loading={loading}
          searchable={false}
          noCard={true}
          pageSize={10}
        />
      </div>
    </div>
  )
}

export default FinancialReport
